import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Alert,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useServices, useDeleteService, type Service } from '@/hooks/useServices';
import { useNotify } from '@/stores/notificationStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/common';
import { formatDuration } from '@/utils/duration';
import { useAuthStore } from '@/stores/authStore';
import { ServiceFormDialog } from './ServiceFormDialog';

const MANAGE_SERVICES_PERMISSION = 'settings.manage_services';

export function ServicesTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Service mutations require settings.manage_services server-side; hide what would 403.
  const canManageServices = hasPermission(MANAGE_SERVICES_PERMISSION);

  const [showInactive, setShowInactive] = useState(false);
  const { data: services, isLoading, error } = useServices(
    showInactive ? undefined : { isActive: true },
  );
  const deleteService = useDeleteService();

  const [formOpen, setFormOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);

  const handleAdd = () => {
    if (!hasPermission(MANAGE_SERVICES_PERMISSION)) return;
    setEditingService(null);
    setFormOpen(true);
  };

  const handleEdit = (service: Service) => {
    if (!hasPermission(MANAGE_SERVICES_PERMISSION)) return;
    setEditingService(service);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingService(null);
  };

  const handleDeleteClick = (service: Service) => {
    if (!hasPermission(MANAGE_SERVICES_PERMISSION)) return;
    setServiceToDelete(service);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!serviceToDelete || !hasPermission(MANAGE_SERVICES_PERMISSION)) return;
    try {
      await deleteService.mutateAsync(serviceToDelete.id);
      notify.success(t('servicesTab.deleteSuccess'), t('servicesTab.deleteSuccessDesc'));
    } catch {
      notify.error(t('servicesTab.deleteFailed'));
    } finally {
      setDeleteConfirmOpen(false);
      setServiceToDelete(null);
    }
  };

  if (error) {
    return <Alert severity="error">{tc('error')}</Alert>;
  }

  const sortCompareFn = (a: Service, b: Service, key: string): number => {
    switch (key) {
      case 'name':
        return a.name.localeCompare(b.name, 'fi');
      case 'duration':
        return (a.estimatedDurationHours ?? 0) - (b.estimatedDurationHours ?? 0);
      case 'status':
        return Number(b.isActive) - Number(a.isActive);
      default:
        return 0;
    }
  };

  const columns: DataTableColumn<Service>[] = [
    {
      header: t('servicesTab.name'),
      sortKey: 'name',
      renderCell: (service) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {service.name}
          </Typography>
          {service.description && (
            <Typography variant="caption" color="text.secondary">
              {service.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      header: t('servicesTab.estimatedDuration'),
      sortKey: 'duration',
      renderCell: (service) => (
        <Typography variant="body2" color="text.secondary">
          {service.estimatedDurationHours
            ? formatDuration(service.estimatedDurationHours)
            : '—'}
        </Typography>
      ),
    },
    {
      header: t('servicesTab.pricingType'),
      renderCell: (service) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={service.pricingType === 'fixed' ? t('servicesTab.pricingFixed') : t('servicesTab.pricingComponent')}
            size="small"
            variant="outlined"
          />
          {service.pricingType === 'fixed' && service.fixedPriceCents != null && (
            <Typography variant="caption">
              {(service.fixedPriceCents / 100).toFixed(2)} €
            </Typography>
          )}
        </Box>
      ),
    },
    {
      header: t('servicesTab.laborItems'),
      renderCell: (service) => (
        <Typography variant="body2" color="text.secondary">
          {service.laborItems.length > 0 ? service.laborItems.length : ''}
        </Typography>
      ),
    },
    {
      header: tc('status'),
      sortKey: 'status',
      align: 'center',
      renderCell: (service) => (
        <Chip
          label={service.isActive ? t('servicesTab.active') : t('servicesTab.inactive')}
          color={service.isActive ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      header: '',
      align: 'right',
      renderCell: (service) => canManageServices ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
          <Tooltip title={tc('edit')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleEdit(service); }}
              aria-label={tc('edit')}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {service.isActive && (
            <Tooltip title={tc('delete')}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleDeleteClick(service); }}
                color="error"
                aria-label={tc('delete')}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) : null,
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h6">{t('servicesTab.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('servicesTab.subtitle')}
          </Typography>
        </Box>
        {canManageServices && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            {t('servicesTab.addService')}
          </Button>
        )}
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => { setShowInactive(e.target.checked); }}
              size="small"
            />
          }
          label={tc('showInactive')}
        />
      </Box>

      <DataTable<Service>
        columns={columns}
        data={services ?? []}
        getRowKey={(service) => service.id}
        {...(canManageServices ? { onRowClick: handleEdit } : {})}
        sortCompareFn={sortCompareFn}
        defaultSortKey="name"
        loading={isLoading}
        emptyMessage={t('servicesTab.noServicesDesc')}
        ariaLabel={t('servicesTab.title')}
        renderMobileCard={(service) => (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {service.name}
                </Typography>
                {service.description && (
                  <Typography variant="caption" color="text.secondary">
                    {service.description}
                  </Typography>
                )}
              </Box>
              <Chip
                label={service.isActive ? t('servicesTab.active') : t('servicesTab.inactive')}
                color={service.isActive ? 'success' : 'default'}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={service.pricingType === 'fixed' ? t('servicesTab.pricingFixed') : t('servicesTab.pricingComponent')}
                  size="small"
                  variant="outlined"
                />
                {service.estimatedDurationHours && (
                  <Typography variant="caption" color="text.secondary">
                    {formatDuration(service.estimatedDurationHours)}
                  </Typography>
                )}
              </Box>
              {canManageServices && (
                <Box>
                  <Tooltip title={tc('edit')}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleEdit(service); }}
                      aria-label={tc('edit')}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {service.isActive && (
                    <Tooltip title={tc('delete')}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(service); }}
                        color="error"
                        aria-label={tc('delete')}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
      />

      <ServiceFormDialog
        open={formOpen}
        onClose={handleFormClose}
        editingService={editingService}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={tc('confirm')}
        message={t('servicesTab.confirmDeactivate')}
        loading={deleteService.isPending}
      />
    </Box>
  );
}
